import { useMemo, useState } from "react";
import { Flex } from "styles/utility-components";

import { SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { SpendExportGroupBy } from "lib/types/graphQLEnums";
import BuyerChart from "../BuyerChart";
import ExportDataButton from "../ExportDataButton";
import { RankingEntity, SpendRankingSort, useRankingTable } from "../hooks/useRankingTable";
import { SpendView } from "../hooks/useSpendUrlState";
import { EmptyState } from "../pageStates";
import SupplierChart from "../SupplierChart";
import { SpendRankingTable } from "./SpendRankingTable";

type Props = {
  filters: SpendFilters;
  entity: RankingEntity;
  view: SpendView;
  onViewChange: (update: Partial<SpendView>) => void;
};

/**
 * With rows ticked, the export covers just those rows. The export API has no row-id input,
 * but ranking rows are keyed by guid, so the guids go in as a filter.
 */
function exportFiltersFor(
  filters: SpendFilters,
  entity: RankingEntity,
  selectedRowKeys: string[],
): SpendFilters {
  if (selectedRowKeys.length === 0) {
    return filters;
  }
  return entity === "buyers"
    ? { ...filters, buyerGuids: selectedRowKeys }
    : { ...filters, supplierGuids: selectedRowKeys };
}

/**
 * Trends grouped by buyers or suppliers: the chart of the top ten, and the full ranking
 * under it. Owns its query, row selection, export and empty state.
 */
export function SpendRankingView({ filters, entity, view, onViewChange }: Props) {
  const { measure, sortOrder, interval, chartType } = view;
  const sort = useMemo<SpendRankingSort>(() => ({ measure, sortOrder }), [measure, sortOrder]);

  // Ticked rows belong to one search and one ranking. SpendTrendsView remounts this view
  // when either changes, which clears them.
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const table = useRankingTable(filters, entity, {
    sort,
    selectedRowKeys,
    onSelectedRowsChange: setSelectedRowKeys,
  });
  const total = table.pagination.total;

  if (!table.isLoading && !table.isError && total === 0) {
    return <EmptyState filters={filters} />;
  }

  const Chart = entity === "buyers" ? BuyerChart : SupplierChart;

  return (
    <Flex column gap={24}>
      <Chart filters={filters} dateUnit={interval} sort={sort} chartType={chartType} />
      <SpendRankingTable
        entity={entity}
        table={table}
        sort={sort}
        onSortChange={onViewChange}
        selectedRowKeys={selectedRowKeys}
        onSelectedRowsChange={setSelectedRowKeys}
        exportAction={
          <ExportDataButton
            filters={exportFiltersFor(filters, entity, selectedRowKeys)}
            totalRows={selectedRowKeys.length > 0 ? selectedRowKeys.length : total ?? 0}
            isLoadingTotalRows={table.isLoading}
            groupBy={entity === "buyers" ? SpendExportGroupBy.Buyer : SpendExportGroupBy.Supplier}
          />
        }
      />
    </Flex>
  );
}
